import * as React from "react";
import { Check, ChevronDown, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * SearchableSelect - A combobox/searchable dropdown component
 * 
 * Features:
 * - Input field serves as both display and search
 * - Typing immediately filters results
 * - Dropdown icon opens full option list
 * - Keyboard navigation (arrow keys, enter, escape)
 * - No separate "Search" row inside the dropdown
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of {value, label} objects
 * @param {string} props.value - Selected value
 * @param {function} props.onValueChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text when no selection
 * @param {string} props.searchPlaceholder - (Deprecated) Now uses placeholder directly
 * @param {string} props.emptyText - Text shown when no results
 * @param {string} props.className - Additional classes for container
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.sortAlphabetically - Sort options A-Z (default true)
 * @param {boolean} props.clearable - Show clear button when value selected (default false)
 * @param {string} props.clearValue - Value to set when cleared (default "")
 * @param {boolean} props.autoOpenOnFocus - Auto-open dropdown on input focus (default true)
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder, // Deprecated - kept for backwards compatibility
  emptyText = "No results found.",
  className,
  disabled = false,
  sortAlphabetically = true,
  clearable = false,
  clearValue = "",
  autoOpenOnFocus = true,
  onCreateNew, // Optional: (searchText) => void — shows "+ Create" row in dropdown
  createLabel, // Optional: custom label function (searchText) => string
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Sort options alphabetically by label if enabled
  const sortedOptions = React.useMemo(() => {
    if (!sortAlphabetically) return options;
    return [...options].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" })
    );
  }, [options, sortAlphabetically]);

  // Filter options based on input
  const filteredOptions = React.useMemo(() => {
    if (!inputValue.trim()) return sortedOptions;
    const search = inputValue.toLowerCase().trim();
    return sortedOptions.filter((opt) =>
      (opt.label || "").toLowerCase().includes(search)
    );
  }, [sortedOptions, inputValue]);

  // Find selected option label
  const selectedOption = React.useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [options, value]);

  // Display value in input
  const displayValue = React.useMemo(() => {
    if (open) return inputValue;
    return selectedOption?.label || "";
  }, [open, inputValue, selectedOption]);

  // Reset highlighted index when filtered options change
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setInputValue("");
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (open && listRef.current) {
      const highlightedEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!open) setOpen(true);
    setHighlightedIndex(0);
  };

  const handleInputFocus = () => {
    if (!disabled && autoOpenOnFocus) {
      setOpen(true);
      setInputValue("");
    }
  };

  const handleInputClick = () => {
    if (!disabled && !open) {
      setOpen(true);
      setInputValue("");
    }
  };

  const handleSelect = (option) => {
    onValueChange(option.value);
    setOpen(false);
    setInputValue("");
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onValueChange(clearValue);
    setInputValue("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (open && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (!open) {
          setOpen(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setInputValue("");
        break;
      case "Tab":
        if (open) {
          setOpen(false);
          setInputValue("");
        }
        break;
      default:
        break;
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      if (open) {
        setOpen(false);
        setInputValue("");
      } else {
        setOpen(true);
        inputRef.current?.focus();
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      {...props}
    >
      {/* Input container */}
      <div
        className={cn(
          "flex h-9 w-full items-center rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors",
          "focus-within:ring-1 focus-within:ring-ring",
          disabled && "cursor-not-allowed opacity-50",
          open && "ring-1 ring-ring"
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 h-full px-3 py-2 bg-transparent outline-none placeholder:text-muted-foreground",
            "disabled:cursor-not-allowed"
          )}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        
        {/* Clear button */}
        {clearable && value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 hover:bg-muted rounded mr-1"
            tabIndex={-1}
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
        
        {/* Dropdown toggle */}
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={cn(
            "px-2 h-full flex items-center justify-center border-l border-input",
            "hover:bg-muted/50 transition-colors",
            disabled && "pointer-events-none"
          )}
          tabIndex={-1}
          aria-label="Toggle dropdown"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </div>

      {/* Dropdown list */}
      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md",
            "animate-in fade-in-0 zoom-in-95"
          )}
        >
          <ul
            ref={listRef}
            role="listbox"
            className="max-h-[240px] overflow-y-auto p-1"
          >
            {filteredOptions.length === 0 && !onCreateNew ? (
              <li className="py-3 px-2 text-center text-sm text-muted-foreground">
                {emptyText}
              </li>
            ) : (
              <>
                {filteredOptions.length === 0 && onCreateNew && (
                  <li className="py-2 px-2 text-center text-sm text-muted-foreground">
                    {emptyText}
                  </li>
                )}
                {filteredOptions.map((option, index) => (
                  <li
                    key={option.value}
                    data-index={index}
                    role="option"
                    aria-selected={value === option.value}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
                      "transition-colors",
                      highlightedIndex === index && "bg-accent text-accent-foreground",
                      value === option.value && "font-medium"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </li>
                ))}
              </>
            )}
            {onCreateNew && (
              <li
                role="option"
                onClick={() => {
                  const text = inputValue.trim();
                  setOpen(false);
                  setInputValue("");
                  onCreateNew(text);
                }}
                className={cn(
                  "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none",
                  "transition-colors hover:bg-accent hover:text-accent-foreground",
                  "border-t border-border mt-1 pt-2 text-primary font-medium"
                )}
                data-testid="create-new-option"
              >
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">
                  {createLabel
                    ? createLabel(inputValue.trim())
                    : inputValue.trim()
                      ? `Create "${inputValue.trim()}"`
                      : '+ Create New'}
                </span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
